import { Grid, IconButton, Typography } from '@mui/material'
import { ButtonV2, TextInputV4 } from '@labrav/react-components'
import { useStyles } from './OfferRecipientV3.styles'
import React, { useEffect, useState } from 'react'
import { FormikProps, FieldArray } from 'formik'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { get } from 'lodash'
import { getRandom } from '../../utils/helperFunctions'
import { OfferRecipients } from '../../../types/flyout'
import clsx from 'clsx'

export const OfferRecipientV3 = React.memo(
  ({
    formik,
    isReadOnly = false,
    borderLess = false,
    keyName = 'offerRecipients',
  }: {
    formik: FormikProps<Record<string, unknown>>
    isReadOnly?: boolean
    borderLess?: boolean
    keyName?: string
  }) => {
    const { values, errors, touched, handleBlur, handleChange } = formik
    const { classes } = useStyles({ borderLess })
    const [randomKey, setRandomKey] = useState<number>(getRandom())
    const offerRecipients = (values[keyName] || []) as OfferRecipients[]

    const offerRecipientsLength = offerRecipients?.length

    useEffect(() => {
      setRandomKey(getRandom())
    }, [offerRecipients.length])

    return (
      <>
        <div
          className={clsx(
            classes.recipientsRowsContainer,
            borderLess ? classes.borderNone : ''
          )}
          data-testid="update-recipients"
        >
          <FieldArray name={keyName}>
            {({ push, remove }) => (
              <>
                {offerRecipients.map((recipient, index) => (
                  <div
                    className={clsx(
                      classes.updateRow,
                      index === offerRecipientsLength - 1
                        ? classes.lastRecipient
                        : '',
                      index === 0 ? classes.firstRecipient : '',
                      classes.borderRecipient
                    )}
                    data-testid={'recipient-row'}
                  >
                    <div className={classes.recipientTitleWrapper}>
                      <Typography className={classes.subTitle}>
                        Recipient {index + 1}
                      </Typography>
                      <div>
                        <RemoveRecipient
                          index={index}
                          recipientCount={offerRecipients.length}
                          remove={remove}
                          smallSize={true}
                        />
                      </div>
                    </div>

                    <Grid
                      container
                      spacing={3}
                      width={'100%'}
                      className={clsx(classes.recipientWidth)}
                    >
                      <Grid
                        key={`recipient-${randomKey}-firstName`}
                        size={{
                          md: 6,
                          lg: 6,
                          sm: 6,
                        }}
                      >
                        <TextInputV4
                          placeHolder=""
                          isRequired={false}
                          isReadOnly={isReadOnly}
                          keyName={`${keyName}.${index}.firstName`}
                          onChange={handleChange}
                          type="string"
                          title="First name"
                          value={recipient.firstName || ''}
                          isError={
                            !!(
                              get(
                                touched,
                                `${keyName}.${[index]}.firstName`,
                                false
                              ) &&
                              get(
                                errors,
                                `${keyName}.${[index]}.firstName`,
                                false
                              )
                            )
                          }
                          errorText={
                            (!!get(
                              touched,
                              `${keyName}.${[index]}.firstName`,
                              false
                            ) &&
                              get(
                                errors,
                                `${keyName}.${[index]}.firstName`,
                                ''
                              )) ||
                            ''
                          }
                          onBlur={handleBlur}
                        />
                      </Grid>
                      <Grid
                        key={`recipient-${randomKey}-lastName`}
                        size={{
                          md: 6,
                          lg: 6,
                          sm: 6,
                        }}
                      >
                        <TextInputV4
                          placeHolder=""
                          isRequired={false}
                          isReadOnly={isReadOnly}
                          keyName={`${keyName}.${index}.lastName`}
                          onChange={handleChange}
                          type="string"
                          title="Last name"
                          value={recipient.lastName || ''}
                          isError={
                            !!(
                              get(
                                touched,
                                `${keyName}.${index}.lastName`,
                                false
                              ) &&
                              get(errors, `${keyName}.${index}.lastName`, false)
                            )
                          }
                          errorText={
                            (!!get(
                              touched,
                              `${keyName}.${index}.lastName`,
                              false
                            ) &&
                              get(
                                errors,
                                `${keyName}.${index}.lastName`,
                                ''
                              )) ||
                            ''
                          }
                          onBlur={handleBlur}
                        />
                      </Grid>
                      <Grid
                        key={`recipient-${randomKey}-title`}
                        size={{
                          md: 6,
                          lg: 6,
                          sm: 6,
                        }}
                      >
                        <TextInputV4
                          placeHolder=""
                          isRequired={false}
                          isReadOnly={isReadOnly}
                          keyName={`${keyName}.${index}.title`}
                          onChange={handleChange}
                          type="string"
                          title="Title (Optional)"
                          value={recipient.title || ''}
                          isError={
                            !!(
                              get(
                                touched,
                                `${keyName}.${index}.title`,
                                false
                              ) &&
                              get(errors, `${keyName}.${index}.title`, false)
                            )
                          }
                          errorText={
                            (!!get(
                              touched,
                              `${keyName}.${index}.title`,
                              false
                            ) &&
                              get(errors, `${keyName}.${index}.title`, '')) ||
                            ''
                          }
                          onBlur={handleBlur}
                        />
                      </Grid>
                      <Grid
                        key={`recipient-${randomKey}-email`}
                        size={{
                          md: 6,
                          lg: 6,
                          sm: 6,
                        }}
                      >
                        <TextInputV4
                          placeHolder=""
                          isRequired={false}
                          isReadOnly={isReadOnly}
                          keyName={`${keyName}.${index}.email`}
                          onChange={handleChange}
                          type="string"
                          title="Email"
                          value={recipient.email || ''}
                          isError={
                            !!(
                              get(
                                touched,
                                `${keyName}.${[index]}.email`,
                                false
                              ) &&
                              get(errors, `${keyName}.${[index]}.email`, false)
                            )
                          }
                          errorText={
                            (!!get(
                              touched,
                              `${keyName}.${[index]}.email`,
                              false
                            ) &&
                              get(errors, `${keyName}.${[index]}.email`, '')) ||
                            ''
                          }
                          onBlur={handleBlur}
                        />
                      </Grid>
                    </Grid>
                  </div>
                ))}

                <ButtonV2
                  className={classes.addRecipientBtn}
                  data-testid={'add-a-recipient'}
                  onClick={() =>
                    push({
                      email: '',
                      firstName: '',
                      lastName: '',
                      title: '',
                    })
                  }
                  styleType="link"
                >
                  <FontAwesomeIcon
                    icon={faPlus}
                    width={'14px'}
                    height={'16px'}
                    style={{ paddingRight: '13px', paddingLeft: '5px' }}
                  />
                  Add recipients
                </ButtonV2>
              </>
            )}
          </FieldArray>
        </div>
      </>
    )
  }
)

export const RemoveRecipient = ({
  recipientCount,
  index,
  remove,
  smallSize = false,
  deleteVisibeForAll = false,
}: {
  recipientCount: number
  index: number
  remove: <X = any>(index: number) => X | undefined
  smallSize?: boolean
  deleteVisibeForAll?: boolean
}) => {
  const { classes } = useStyles({})
  return recipientCount > 1 || deleteVisibeForAll ? (
    <IconButton
      className={clsx(
        !smallSize ? classes.closeIconButton : classes.removeIconPadding
      )}
      onClick={() => remove(index)}
      data-testid="cancel-button"
      size="large"
    >
      <FontAwesomeIcon icon={faTrash} width={'16px'} height={'18px'} />
    </IconButton>
  ) : (
    <></>
  )
}
